import { Box, Button, Grid, IconButton, LinearProgress, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import React, { MouseEvent } from 'react';
import { useDebounce } from 'usehooks-ts';
import useGetProducts from 'hooks/useGetProducts';
import { ProductsProvider } from 'stores/useProducts';
import { useServerTranslation } from 'hooks/useServerTranslation';
import { deprecatedColors } from '../../../styles/theme/deprecatedColors';
import { ProductPreview } from './ProductPreview';

type PopupSearchResultsProps = {
    searchValue: string;
    handleShowSearchResults: (event: MouseEvent<HTMLButtonElement>) => void;
    handleSearchFieldClear: () => void;
};

export function PopupSearchResults(popupProps: PopupSearchResultsProps) {
    return (
        <ProductsProvider>
            <ProvidedPopupSearchResults {...popupProps} />
        </ProductsProvider>
    );
}

export function ProvidedPopupSearchResults(popupProps: PopupSearchResultsProps) {
    const { searchValue, handleShowSearchResults, handleSearchFieldClear } = popupProps;

    const { t } = useServerTranslation();

    const debouncedSearchValue = useDebounce<string>(searchValue, 500);

    const { products, message, loading } = useGetProducts({
        slug: debouncedSearchValue,
        searchValue: debouncedSearchValue,
    });
    if (message) {
        return <>{message}</>;
    }

    return (
        <Box
            style={{
                backgroundColor: 'white',
                width: '100%',
                left: '0',
            }}
        >
            <Box
                flexGrow={1}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
                <Box>
                    <Typography
                        variant="h4"
                        component="div"
                        paddingRight={5}
                        borderBottom={`1.5px solid ${deprecatedColors.secondary2}`}
                        paddingBottom="8px"
                    >
                        {t('menu.search-results')}
                    </Typography>
                </Box>
                <Box paddingBottom="10px">
                    <IconButton
                        edge="start"
                        color="inherit"
                        aria-label="menu"
                        onClick={handleSearchFieldClear}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Box>
            <Grid item overflow="auto" flexGrow={4} maxHeight="50dvh">
                {(loading || searchValue !== debouncedSearchValue) && <LinearProgress sx={{ height: 8 }} />}
                {products.map((product, index) => {
                    if (!product) {
                        return null;
                    }
                    const { images } = product;
                    const [image] = images ?? [];

                    return (
                        <ProductPreview
                            key={product.id}
                            product={product}
                            index={index}
                            image={image}
                        />
                    );
                })}
            </Grid>
            <Grid item flexGrow={1} display="flex" justifyContent="center" marginTop={1}>
                <Button
                    variant="contained"
                    sx={{ borderRadius: '20px', paddingX: '30px' }}
                    onClick={(event) => {
                        handleShowSearchResults(event);
                    }}
                >
                    {t('common.button.show-more-products')}
                </Button>
            </Grid>
        </Box>
    );
}
